import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import {
  FiArrowLeft, FiChevronLeft, FiChevronRight, FiTarget,
  FiBarChart2, FiCalendar, FiUser, FiUsers, FiAward,
  FiChevronDown, FiChevronUp
} from 'react-icons/fi';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const IconSwords = ({ size = 20, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="14.5 17.5 3 5 3 3 5 3 17.5 14.5" />
    <line x1="13" y1="19" x2="19" y2="13" />
    <line x1="16" y1="16" x2="20" y2="20" />
    <line x1="19" y1="21" x2="21" y2="19" />
    <polyline points="9.5 17.5 21 5 21 3 19 3 6.5 14.5" />
    <line x1="11" y1="19" x2="5" y2="13" />
  </svg>
);

const WorkoutDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  // Profilképek tárolása
  const [profileImages, setProfileImages] = useState({ me: null, opponent: null });

  // Navigációs állapotok
  const [currentSeries, setCurrentSeries] = useState(1);
  const [participantIndex, setParticipantIndex] = useState(0);
  const [isAverageInPercentage, setIsAverageInPercentage] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        fetchWorkoutData(user.uid);
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [id]);

  const fetchWorkoutData = async (uid) => {
    try {
      const docRef = doc(db, 'users', uid, 'myworkouts', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.id ? { id: docSnap.id, ...docSnap.data() } : docSnap.data();
        setWorkout(data);

        // Profilképek lekérése
        fetchProfileImages(uid, data);
      } else {
        alert("Az edzés nem található!");
        navigate('/edzeseim');
      }
    } catch (error) {
      console.error("Hiba az adatok betöltésekor:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfileImages = async (myUid, workoutData) => {
    try {
      // Saját kép
      const meRef = doc(db, 'users', myUid);
      const meSnap = await getDoc(meRef);
      const meImg = meSnap.exists() ? meSnap.data().profileImageUrl : null;

      // Ellenfél képe (ha Rival)
      let oppImg = null;
      if (workoutData.type === 'rival' && workoutData.participants?.length > 0) {
        const oppUid = workoutData.participants[0].uid;
        if (oppUid) {
          const oppRef = doc(db, 'users', oppUid);
          const oppSnap = await getDoc(oppRef);
          oppImg = oppSnap.exists() ? oppSnap.data().profileImageUrl : null;
        }
      }

      setProfileImages({ me: meImg, opponent: oppImg });
    } catch (e) {
      console.error("Hiba a képek lekérésekor:", e);
    }
  };

  // Győztes meghatározása (Rival módhoz)
  const winnerInfo = useMemo(() => {
    if (!workout || workout.type !== 'rival' || !workout.participants?.length) return null;

    const myTotal = workout.totalScore;
    const oppTotal = workout.participants[0].totalScore;

    if (myTotal > oppTotal) return 'me';
    if (oppTotal > myTotal) return 'opponent';

    // Holtverseny esetén: Countback (11 -> 10 -> 8...)
    const zones = ['11', '10', '8', '5'];
    for (const z of zones) {
      const myCount = workout.shots.filter(s => s.value === z).length;
      const oppCount = workout.participants[0].shots.filter(s => s.value === z).length;
      if (myCount > oppCount) return 'me';
      if (oppCount > myCount) return 'opponent';
    }

    // Ha még mindig egyenlő: kevesebb 0 (M)
    const myMisses = workout.shots.filter(s => s.value === '0' || s.value === 'M').length;
    const oppMisses = workout.participants[0].shots.filter(s => s.value === '0' || s.value === 'M').length;
    if (myMisses < oppMisses) return 'me';
    if (oppMisses < myMisses) return 'opponent';

    return 'draw';
  }, [workout]);

  // Aktuális résztvevő kiválasztása (Csak Group és Competition esetén)
  const currentParticipant = useMemo(() => {
    if (!workout) return null;

    if (workout.type === 'rival') {
        // Rival módban mindkettőt egyszerre mutatjuk a bogyóknál,
        // de az alap statisztikákhoz visszadjuk a kiválasztottat
        return participantIndex === 0 ? {
            name: "Én",
            bowCategory: workout.bowCategory,
            shots: workout.shots || [],
            totalScore: workout.totalScore,
            average: workout.averageArrowScore
        } : workout.participants[0];
    }

    const participants = workout.participants || [];
    if (participants.length > 0) {
      return participants[participantIndex];
    }

    // Fallback Solo módra
    return {
      name: "Saját eredmény",
      bowCategory: workout.bowCategory,
      shots: workout.shots || [],
      totalScore: workout.totalScore || 0,
      average: workout.averageArrowScore || 0
    };
  }, [workout, participantIndex]);

  // Statisztikai színek és ikonok
  const getStyle = (type) => {
    switch (type) {
      case 'group': return { color: '#2196F3', icon: <FiUsers /> };
      case 'rival': return { color: '#E53935', icon: <IconSwords /> };
      case 'competition': return { color: '#FFD700', icon: <FiAward /> };
      default: return { color: '#4CAF50', icon: <FiUser /> };
    }
  };

  const getColorForValue = (v) => {
    const num = parseInt(v);
    if (v === 'X' || num > 10) return '#4CAF50'; // Zöld
    if (num === 10 || num === 9) return '#FFD700'; // Arany
    if (num === 8 || num === 7) return '#F44336'; // Piros
    if (num === 6 || num === 5) return '#1976D2'; // Kék
    return '#9E9E9E'; // Szürke
  };

  // Pontszám eltérés számítása
  const calculateDifference = (p) => {
    if (!workout || !p) return null;
    const filledShots = (p.shots || []).filter(s => s.value !== "").length;
    if (filledShots === 0) return null;

    const numericValues = (workout.possibleHitValues || []).map(v => parseInt(v)).filter(v => !isNaN(v));
    const naturalMax = Math.max(...numericValues, 0);
    const referenceMax = naturalMax > 10 ? 10 : (naturalMax || 1);

    const expectedMax = filledShots * referenceMax;
    const diff = p.totalScore - expectedMax;

    if (diff === 0) return null;
    const color = diff > 0 ? '#4CAF50' : '#F44336';
    return <span style={{ color, marginLeft: '5px' }}>({diff > 0 ? '+' : ''}{diff})</span>;
  };

  // Grafikon adatok előkészítése
  const chartData = useMemo(() => {
    if (!workout) return [];
    const participants = workout.participants && workout.participants.length > 0
      ? workout.participants
      : [{ name: "Én", shots: workout.shots || [] }];

    const maxShots = Math.max(...participants.map(p => p.shots.filter(s => s.value !== "").length));
    const data = [];

    for (let i = 0; i < maxShots; i++) {
      const entry = { name: i + 1 };
      participants.forEach(p => {
        const shotsUpToI = p.shots.filter(s => s.value !== "").slice(0, i + 1);
        const sum = shotsUpToI.reduce((acc, s) => acc + (s.sum || 0), 0);
        entry[p.name] = parseFloat((sum / (i + 1)).toFixed(2));
      });
      data.push(entry);
    }
    return data;
  }, [workout]);

  const participantColors = ['#4CAF50', '#2196F3', '#FFD700', '#F44336', '#9C27B0', '#00BCD4'];

  if (loading) return <div style={centerStyle}>Adatok betöltése... 🏹</div>;
  if (!workout) return <div style={centerStyle}>Hiba történt.</div>;

  const style = getStyle(workout.type);
  const date = workout.createdAt?.toDate ? workout.createdAt.toDate() : new Date(workout.createdAt);

  return (
    <div style={containerStyle} className="force-light-theme">
      {/* VISSZA GOMB ÉS CÍM */}
      <div style={headerNavStyle}>
        <button onClick={() => navigate('/edzeseim')} style={backBtnStyle}>
          <FiArrowLeft size={20} /> Vissza
        </button>

        {workout.type === 'rival' ? (
          /* PÁRBAJ SPECIÁLIS FEJLÉC */
          <div style={rivalHeaderContainer}>
            <div style={rivalProfileBlock}>
              <div style={profileCircleWrapper}>
                <img src={profileImages.me || "https://via.placeholder.com/80"} alt="Én" style={profileImgStyle} />
                {winnerInfo === 'me' && <div style={trophyBadgeStyle}><FiAward size={14} /></div>}
              </div>
              <span style={rivalNameStyle}>Én</span>
              <span style={rivalScoreStyle}>{workout.totalScore}</span>
            </div>

            <div style={rivalVsStyle}>VS</div>

            <div style={rivalProfileBlock}>
              <div style={profileCircleWrapper}>
                <img src={profileImages.opponent || "https://via.placeholder.com/80"} alt="Ellenfél" style={profileImgStyle} />
                {winnerInfo === 'opponent' && <div style={trophyBadgeStyle}><FiAward size={14} /></div>}
              </div>
              <span style={rivalNameStyle}>{workout.participants[0]?.name || 'Ellenfél'}</span>
              <span style={rivalScoreStyle}>{workout.participants[0]?.totalScore || 0}</span>
            </div>
          </div>
        ) : (
          /* NORMÁL FEJLÉC */
          <>
            <div style={titleRowStyle}>
              <div style={{ ...typeIndicatorStyle, backgroundColor: style.color }}>{style.icon}</div>
              <h2 style={workoutTitleStyle}>{workout.workoutName || 'Verseny'}</h2>
            </div>
            <div style={dateLabelStyle}>
              <FiCalendar size={14} />
              {date.toLocaleDateString('hu-HU')} {date.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </>
        )}
      </div>

      {/* NAVIGÁCIÓS PANEL */}
      <div style={navPanelStyle}>
        {/* Cél váltó */}
        <div style={navRowStyle}>
          <button
            style={navCircleBtnStyle}
            disabled={currentSeries <= 1}
            onClick={() => setCurrentSeries(prev => prev - 1)}
          >
            <FiChevronLeft size={24} />
          </button>
          <div style={navLabelStyle}>
            Cél: <span style={{ color: '#b30000', marginLeft: '5px' }}>{currentSeries} / {workout.targetCount}</span>
          </div>
          <button
            style={navCircleBtnStyle}
            disabled={currentSeries >= workout.targetCount}
            onClick={() => setCurrentSeries(prev => prev + 1)}
          >
            <FiChevronRight size={24} />
          </button>
        </div>

        {/* Résztvevő váltó (Csak ha nem Solo) */}
        {workout.participants && workout.participants.length > 1 && (
          <div style={{ ...navRowStyle, marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '10px' }}>
            <button
              style={navCircleBtnStyle}
              disabled={participantIndex <= 0}
              onClick={() => setParticipantIndex(prev => prev - 1)}
            >
              <FiChevronLeft size={24} />
            </button>
            <div style={{ ...navLabelStyle, fontSize: '16px' }}>
              {currentParticipant.name}
            </div>
            <button
              style={navCircleBtnStyle}
              disabled={participantIndex >= workout.participants.length - 1}
              onClick={() => setParticipantIndex(prev => prev + 1)}
            >
              <FiChevronRight size={24} />
            </button>
          </div>
        )}
      </div>

      {/* AKTUÁLIS LÖVÉSEK (Bogyók) */}
      <div style={shotsCardStyle}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>

          {/* SAJÁT LÖVÉSEK (Vagy aktuális résztvevő ha nem Rival) */}
          <div>
            {workout.type === 'rival' && <div style={smallLabelStyle}>Én</div>}
            <div style={shotsContainerStyle}>
              {Array.from({ length: workout.shotsPerTarget || 1 }).map((_, i) => {
                const seriesShots = (workout.type === 'rival' ? workout.shots : currentParticipant.shots || []).filter(s => s.target === currentSeries);
                const shot = seriesShots[i];
                const val = shot?.value || "-";
                const isFilled = val !== "-";
                return <div key={i} style={shotCircleStyle(isFilled, true)}>{val}</div>;
              })}
            </div>
          </div>

          {/* ELLENFÉL LÖVÉSEI (Csak Rival mód) */}
          {workout.type === 'rival' && workout.participants?.length > 0 && (
            <div style={{ borderTop: '1px dashed #eee', paddingTop: '10px' }}>
              <div style={smallLabelStyle}>{workout.participants[0].name}</div>
              <div style={shotsContainerStyle}>
                {Array.from({ length: workout.shotsPerTarget || 1 }).map((_, i) => {
                  const seriesShots = (workout.participants[0].shots || []).filter(s => s.target === currentSeries);
                  const shot = seriesShots[i];
                  const val = shot?.value || "-";
                  const isFilled = val !== "-";
                  return <div key={i} style={shotCircleStyle(isFilled, false)}>{val}</div>;
                })}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* STATISZTIKA PANEL */}
      <div style={statsCardStyle}>
        <div style={statsRowStyle}>
          <div style={scoreBoxStyle}>
            <div style={statLabelStyle}>PONTSZÁM</div>
            <div style={statValueStyle}>
              {currentParticipant.totalScore}
              {calculateDifference(currentParticipant)}
            </div>
          </div>

          <div
            style={averageBtnStyle}
            onClick={() => setIsAverageInPercentage(!isAverageInPercentage)}
          >
            <div style={{ ...statLabelStyle, color: '#fff', opacity: 0.8 }}>ÁTLAG</div>
            <div style={{ ...statValueStyle, color: '#fff', fontSize: '20px' }}>
              {isAverageInPercentage ? (() => {
                const numericValues = (workout.possibleHitValues || []).map(v => parseInt(v)).filter(v => !isNaN(v));
                const naturalMax = Math.max(...numericValues, 0);
                const ref = naturalMax > 10 ? 10 : (naturalMax || 1);
                return ((currentParticipant.average / ref) * 100).toFixed(1) + "%";
              })() : currentParticipant.average.toFixed(2)}
            </div>
          </div>
        </div>

        {/* ZÓNÁK */}
        <div style={zonesContainerStyle}>
          {(workout.possibleHitValues || [])
            .sort((a, b) => (parseInt(b) || 0) - (parseInt(a) || 0))
            .map(val => {
              const count = (currentParticipant.shots || []).filter(s => s.value === val).length;
              return (
                <div key={val} style={zoneItemStyle}>
                  <div style={zoneBadgeStyle(val)}>{val}</div>
                  <div style={zoneCountStyle}>{count}</div>
                </div>
              );
            })}
        </div>
      </div>

      {/* GRAFIKON */}
      <div style={chartCardStyle}>
        <h4 style={cardTitleStyle}>Teljesítmény görbe</h4>
        <div style={{ height: '220px', width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" hide />
              <YAxis domain={[0, 'auto']} width={30} />
              <Tooltip />
              <Legend iconType="circle" />
              {(workout.participants && workout.participants.length > 0 ? workout.participants : [{name: "Én"}]).map((p, idx) => (
                <Line
                  key={p.name}
                  type="monotone"
                  dataKey={p.name}
                  stroke={participantColors[idx % participantColors.length]}
                  strokeWidth={p.name === currentParticipant.name ? 4 : 1.5}
                  dot={false}
                  activeDot={{ r: 6 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* LISTA NÉZET */}
      <div style={historyCardStyle}>
        <div style={historyHeaderStyle} onClick={() => setShowHistory(!showHistory)}>
          <span style={{ fontWeight: 'bold' }}>Találatok (lista nézet)</span>
          {showHistory ? <FiChevronUp /> : <FiChevronDown />}
        </div>

        {showHistory && (
          <div style={historyListStyle}>
            {(() => {
              // Adatok csoportosítása (Saját és Ellenfél ha Rival)
              const myShots = workout.type === 'rival' ? workout.shots : currentParticipant.shots;
              const oppShots = workout.type === 'rival' ? workout.participants[0]?.shots : null;

              const grouped = (myShots || []).reduce((acc, s) => {
                if (s.value === "") return acc;
                if (!acc[s.target]) acc[s.target] = { mine: [], opp: [] };
                acc[s.target].mine.push(s);
                return acc;
              }, {});

              if (oppShots) {
                oppShots.forEach(s => {
                  if (s.value === "") return;
                  if (!grouped[s.target]) grouped[s.target] = { mine: [], opp: [] };
                  grouped[s.target].opp.push(s);
                });
              }

              let myRunningTotal = 0;
              return Object.keys(grouped).sort((a, b) => a - b).map(targetNum => {
                const data = grouped[targetNum];
                const seriesScore = data.mine.reduce((acc, s) => acc + (s.sum || 0), 0);
                myRunningTotal += seriesScore;

                return (
                  <div key={targetNum} style={historyRowStyle}>
                    <div style={historyTargetNumStyle}>{targetNum}.</div>
                    <div style={historyShotsRowStyle}>
                      {/* SAJÁT LÖVÉSEK */}
                      <div style={{ display: 'flex', gap: '3px' }}>
                        {data.mine.map((s, idx) => (
                          <div key={idx} style={historyShotBadgeStyle(s.value, true)}>
                            {s.value}
                          </div>
                        ))}
                      </div>

                      {/* VS SZAKASZ (Csak Rival) */}
                      {workout.type === 'rival' && <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#ccc' }}>VS</div>}

                      {/* ELLENFÉL LÖVÉSEI (Csak Rival) */}
                      {workout.type === 'rival' && (
                        <div style={{ display: 'flex', gap: '3px' }}>
                          {data.opp.map((s, idx) => (
                            <div key={idx} style={historyShotBadgeStyle(s.value, false)}>
                              {s.value}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={historyScoreStyle}>
                      <span style={{ color: '#888', fontSize: '10px' }}>{seriesScore}p</span>
                      <span style={{ fontWeight: 'bold', color: '#b30000' }}>{myRunningTotal}</span>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        )}
      </div>

      <div style={{ height: '80px' }} />
    </div>
  );
};

// --- STÍLUSOK ---
const containerStyle = { padding: '15px', maxWidth: '600px', margin: '0 auto', backgroundColor: '#f4f6f8', minHeight: '100vh' };
const headerNavStyle = { marginBottom: '15px' };
const backBtnStyle = { display: 'flex', alignItems: 'center', gap: '5px', border: 'none', background: 'none', color: '#666', fontWeight: 'bold', cursor: 'pointer', marginBottom: '10px' };
const titleRowStyle = { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' };
const typeIndicatorStyle = { width: '32px', height: '32px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' };
const workoutTitleStyle = { margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#333' };
const dateLabelStyle = { fontSize: '12px', color: '#888', display: 'flex', alignItems: 'center', gap: '5px' };

const navPanelStyle = { backgroundColor: '#fff', borderRadius: '12px', padding: '15px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)', marginBottom: '15px' };
const navRowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const navCircleBtnStyle = { width: '40px', height: '40px', borderRadius: '50%', border: '1px solid #ddd', backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };
const navLabelStyle = { fontSize: '18px', fontWeight: 'bold', textAlign: 'center' };

const shotsCardStyle = { backgroundColor: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginBottom: '15px' };
const shotsContainerStyle = { display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' };
const shotCircleStyle = (filled, isMe) => ({
  width: '45px', height: '45px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: '20px', fontWeight: 'bold',
  backgroundColor: filled ? (isMe ? '#b30000' : '#444') : 'transparent',
  color: filled ? '#fff' : '#888',
  border: filled ? '1px solid #000' : '1px solid #ccc'
});

const smallLabelStyle = { fontSize: '10px', color: '#888', fontWeight: 'bold', textAlign: 'center', marginBottom: '5px' };

// Rivals Speciális Stílusok
const rivalHeaderContainer = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '30px', padding: '10px 0' };
const rivalProfileBlock = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' };
const profileCircleWrapper = { position: 'relative', width: '80px', height: '80px' };
const profileImgStyle = { width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', border: '3px solid #fff', boxShadow: '0 4px 8px rgba(0,0,0,0.2)' };
const trophyBadgeStyle = { position: 'absolute', top: '-10px', right: '-10px', backgroundColor: '#FFD700', color: '#fff', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' };
const rivalNameStyle = { fontSize: '14px', fontWeight: 'bold', color: '#333' };
const rivalScoreStyle = { fontSize: '24px', fontWeight: '900', color: '#b30000' };
const rivalVsStyle = { fontSize: '24px', fontWeight: '900', color: '#ddd' };

const statsCardStyle = { backgroundColor: '#fff', borderRadius: '12px', padding: '15px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', marginBottom: '15px' };
const statsRowStyle = { display: 'flex', gap: '10px', marginBottom: '15px' };
const scoreBoxStyle = { flex: 1, padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '10px', border: '1px solid #eee' };
const averageBtnStyle = { flex: 1, padding: '10px', backgroundColor: '#b30000', borderRadius: '10px', cursor: 'pointer', textAlign: 'center' };
const statLabelStyle = { fontSize: '10px', fontWeight: 'bold', color: '#888', marginBottom: '2px' };
const statValueStyle = { fontSize: '24px', fontWeight: '900', color: '#333' };

const zonesContainerStyle = { display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' };
const zoneItemStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', minWidth: '35px' };
const zoneBadgeStyle = (val) => ({
  width: '30px', height: '22px', borderRadius: '6px', fontSize: '10px', fontWeight: 'bold', color: '#fff',
  backgroundColor: val === 'X' ? '#4CAF50' : (parseInt(val) >= 10 ? '#FFD700' : (parseInt(val) >= 7 ? '#F44336' : '#1976D2')),
  display: 'flex', alignItems: 'center', justifyContent: 'center'
});
const zoneCountStyle = { fontSize: '14px', fontWeight: 'bold', color: '#333' };

const chartCardStyle = { backgroundColor: '#fff', borderRadius: '12px', padding: '15px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', marginBottom: '15px' };
const cardTitleStyle = { margin: '0 0 10px 0', fontSize: '14px', color: '#888', fontWeight: 'bold', textTransform: 'uppercase' };

const historyCardStyle = { backgroundColor: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' };
const historyHeaderStyle = { backgroundColor: '#b30000', color: '#fff', padding: '12px 15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' };
const historyListStyle = { padding: '10px' };
const historyRowStyle = { display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #eee' };
const historyTargetNumStyle = { width: '30px', fontWeight: 'bold', color: '#888' };
const historyShotsRowStyle = { flex: 1, display: 'flex', gap: '10px', alignItems: 'center' };
const historyShotBadgeStyle = (val, isMe = true) => {
  const numericValue = parseInt(val);
  let bgColor = isMe ? '#b30000' : '#444'; // Fallback

  if (val === 'X' || numericValue > 10) bgColor = '#4CAF50';
  else if (numericValue === 10 || numericValue === 9) bgColor = '#FFD700';
  else if (numericValue === 8 || numericValue === 7) bgColor = '#F44336';
  else if (numericValue === 6 || numericValue === 5) bgColor = '#1976D2';
  else if (val !== "-" && val !== "") bgColor = '#9E9E9E';

  return {
    width: '24px', height: '24px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', color: '#fff',
    backgroundColor: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center'
  };
};
const historyScoreStyle = { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: '50px' };

const centerStyle = { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: '#666', fontFamily: 'Arial' };

export default WorkoutDetails;
